import React, {useEffect, use, createContext, useState} from "react";
import PropTypes from "prop-types";
import {useQuery} from "@apollo/client";
import {GET_ORGANIZATION_SETTINGS} from "../graphql/organizationQueries";
import {GET_POLICIES} from "../graphql/policyQueries";
import {LIST_ROLES} from "../graphql/roleQueries";
import {LIST_USERS} from "../graphql/userQueries";
import {Loader} from "../components";
import {
  processExactKeyValuePairSettings,
  processJsonObjectSettings,
  processRolesAndUsersSettings,
  processTrueFalseSettings,
} from "../resources/orgSettings";
import {filter, findWhere, isEmpty, map} from "underscore";

export const OrgSettingsContext = createContext(null);

export const OrgSettingsProvider = ({children}) => {
  const [orgSettings, setSettings] = useState({});

  const {data: settings, loading: settingsLoading} = useQuery(
    GET_ORGANIZATION_SETTINGS,
  );

  const ORG_SETTINGS_POLICIES = {
    names: ["incident_delete", "action_delete", "action_deleteMany"],
  };
  const {data: policiesData} = useQuery(GET_POLICIES);
  const {data: rolesData} = useQuery(LIST_ROLES);
  const {data: usersData} = useQuery(LIST_USERS);

  const incidentDeleteAccessPolicy = findWhere(policiesData?.accessPolicies, {
    name: "incident_delete",
  });
  const actionDeleteAccessPolicy = findWhere(policiesData?.accessPolicies, {
    name: "action_delete",
  });
  const actionDeleteManyAccessPolicy = findWhere(policiesData?.accessPolicies, {
    name: "action_deleteMany",
  });
  const incidentUpdateAccessPolicy = findWhere(policiesData?.accessPolicies, {
    name: "incident_update_details",
  });
  const orgPolicies = {
    action_delete: actionDeleteAccessPolicy,
    action_deleteMany: actionDeleteManyAccessPolicy,
    incident_delete: incidentDeleteAccessPolicy,
    incident_update_details: incidentUpdateAccessPolicy,
  };

  const getPolicyRoles = (policies) => {
    return map(policies?.roles, (policyRole) =>
      findWhere(rolesData?.roles, {id: policyRole.roleId}),
    );
  };

  const getCurrentRoles = (setting) => {
    if (setting?.value) {
      const parsedRoleIds = JSON.parse(setting.value);
      const mappedRoles = map(parsedRoleIds, (parsedRoleId) =>
        findWhere(rolesData.roles, {id: parsedRoleId}),
      );

      return filter(mappedRoles, (mappedRole) => mappedRole !== undefined);
    }
    return [];
  };

  useEffect(() => {
    if (
      settings?.organizationSettings &&
      rolesData &&
      usersData &&
      incidentDeleteAccessPolicy &&
      actionDeleteAccessPolicy &&
      incidentUpdateAccessPolicy
    ) {
      const timeframe = findWhere(settings.organizationSettings, {
        key: "complianceTimeframe",
      });
      const durationObject = JSON.parse(timeframe.value);
      const newUserRoleIdsSetting = findWhere(settings.organizationSettings, {
        key: "newUserRoleIds",
      });
      const scenarioPhaseActionRoleIdsSetting = findWhere(
        settings.organizationSettings,
        {
          key: "scenarioPhaseActionRoleIds",
        },
      );
      const defaultLeadershipChatAccessSetting = findWhere(
        settings.organizationSettings,
        {key: "defaultLeadershipChatAccess"},
      );
      const defaultIncidentChatOmissionsSetting = findWhere(
        settings.organizationSettings,
        {key: "defaultIncidentChatOmissions"},
      );
      const canRequestIncidentChatAccessSetting = findWhere(
        settings.organizationSettings,
        {key: "canRequestIncidentChatAccess"},
      );
      const canRequestLeadershipAccessSetting = findWhere(
        settings.organizationSettings,
        {key: "canRequestLeadershipAccess"},
      );
      const responseWorkflowName = findWhere(settings.organizationSettings, {
        key: "responseWorkflowName",
      });
      const workflowName = JSON.parse(responseWorkflowName.value);

      const aiPermissionsSetting = findWhere(settings.organizationSettings, {
        key: "aiPermissions",
      });
      const aiPermissions = processJsonObjectSettings(aiPermissionsSetting);

      const aiModelInferenceProfileSetting = findWhere(
        settings.organizationSettings,
        {key: "aiModelInferenceProfile"},
      );

      const trustedAdvisorModelProfileSetting = findWhere(
        settings.organizationSettings,
        {key: "trustedAdvisorModelProfile"},
      );

      const keyValuePairSettings = processExactKeyValuePairSettings(
        settings.organizationSettings,
        [
          "actionVisibility",
          "complianceCommunicationChannels",
          "conversationsLegalDisclaimer",
          "notificationSignature",
          "orgFullname",
        ],
      );
      const trueFalseSettings = processTrueFalseSettings(
        settings.organizationSettings,
        [
          "MFARequired",
          "newIncidentsRestricted",
          "autoApproveIncidents",
          "attributeTabOnActions",
          "expandActivityEditor",
          "metricsInIncidents",
          "moreActivitiesOnEvents",
          "editActivitiesAllowed",
          "enableIncidentLeadershipChat",
          "enableIncidentChat",
          "disableMobileScreenCapture",
          "canDownloadDocuments",
        ],
      );

      const closedIncidentsReadOnlyPermissionsSetting = findWhere(
        settings.organizationSettings,
        {
          key: "closedIncidentsReadOnlyPermissions",
        },
      );
      const closedIncidentsReadOnlyPermissions = processJsonObjectSettings(
        closedIncidentsReadOnlyPermissionsSetting,
      );

      const orgSettings = {
        ...keyValuePairSettings,
        ...trueFalseSettings,
        complianceTimeframeSetting: timeframe,
        [timeframe.key]: durationObject.months + " months",
        newUserRoleIds: getCurrentRoles(newUserRoleIdsSetting),
        newUserRoleIdsSetting,
        scenarioPhaseActionRoleIds: getCurrentRoles(
          scenarioPhaseActionRoleIdsSetting,
        ),
        scenarioPhaseActionRoleIdsSetting,
        defaultLeadershipChatAccess: processRolesAndUsersSettings(
          defaultLeadershipChatAccessSetting,
        ),
        defaultLeadershipChatAccessSetting,
        defaultIncidentChatOmissions: processRolesAndUsersSettings(
          defaultIncidentChatOmissionsSetting,
        ),
        defaultIncidentChatOmissionsSetting,
        canRequestIncidentChatAccess: processRolesAndUsersSettings(
          canRequestIncidentChatAccessSetting,
        ),
        canRequestIncidentChatAccessSetting,
        canRequestLeadershipAccess: processRolesAndUsersSettings(
          canRequestLeadershipAccessSetting,
        ),
        canRequestLeadershipAccessSetting,
        policyRoles: {
          incident_delete: getPolicyRoles(incidentDeleteAccessPolicy),
          action_delete: getPolicyRoles(actionDeleteAccessPolicy),
          incident_update_details: getPolicyRoles(incidentUpdateAccessPolicy),
        },
        [responseWorkflowName.key]: {
          upper: workflowName.workflowName,
          lower: workflowName.workflowNameLower,
        },
        responseWorkflowNameSetting: responseWorkflowName,
        aiPermissionsSetting,
        aiPermissions,
        aiModelInferenceProfile: aiModelInferenceProfileSetting?.value,
        aiModelInferenceProfileSetting,
        trustedAdvisorModelProfile: trustedAdvisorModelProfileSetting?.value,
        closedIncidentsReadOnlyPermissions,
        closedIncidentsReadOnlyPermissionsSetting,
      };

      setSettings(orgSettings);
    }
  }, [
    settings,
    rolesData,
    usersData,
    policiesData,
    orgPolicies?.incident_delete,
  ]);

  const providerValues = {
    ...orgSettings,
    allAccessPolicies: policiesData?.accessPolicies ?? [],
    getCurrentRoles,
    orgPolicies,
    ORG_SETTINGS_POLICIES,
    rolesData,
    usersData,
    settingsLoading,
  };

  if (isEmpty(orgSettings)) return <Loader fullViewingHeight />;

  return (
    <OrgSettingsContext value={providerValues}>
      {children}
    </OrgSettingsContext>
  );
};

export const useOrgSettings = () => {
  const context = use(OrgSettingsContext);
  if (!context) {
    throw new Error("Context must be used within the correct provider");
  }
  return context;
};

OrgSettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
